import {
  onAuthStateChanged,
  signInAnonymously,
  User,
  UserCredential,
} from "firebase/auth";
import { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebase/firebase";
import UserAccountService from "@/services/user-account.service";

export default function useFirebaseAuth() {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clear = () => {
    setAuthUser(null);
    setIsLoading(false);
  };

  const authStateChanged = async (user: any) => {
    setIsLoading(true);

    if (!user) {
      // Anonymously sign-in user for tenant
      const userCredential: UserCredential = await signInAnonymously(auth);

      // Create User account
      await UserAccountService.createAnonymousUserAccount(
        String(userCredential.user.tenantId),
        userCredential.user
      ).catch((err) => {
        console.error(err);
        alert(err);
      });

      setIsLoading(false);
      return;
    }

    setAuthUser(user);
    setIsLoading(false);
  };

  const signOut = () => auth.signOut().then(() => clear());

  // Listen for Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, authStateChanged);
    return () => unsubscribe();
  }, []);

  return {
    authUser,
    isLoading,
    signOut,
  };
}

const AuthUserContext = createContext<{
  authUser: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}>({
  authUser: null,
  isLoading: true,
  signOut: async () => {
    /* EMPTY */
  },
});

export function AuthUserProvider({ children }: any) {
  const auth = useFirebaseAuth();
  return (
    <AuthUserContext.Provider value={auth}>{children}</AuthUserContext.Provider>
  );
}

export const useAuth = () =>
  useContext<{
    authUser: User | null;
    isLoading: boolean;
    signOut: () => Promise<void>;
  }>(AuthUserContext);
